import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { colors, mixins } from 'ech-components/src/core/styles/theme/default';
import { Hidden, Container, Spacer } from 'ech-components/src/helpers';
import { Link, Hr, BundesBanner } from 'ech-components/src/primitives';
import { Metanav } from 'ech-components/src/components';
import { useNavigationQuery } from '@queries/useNavigationQuery';
import { createRoutingURI, getRootSlug } from '@scripts/utils/domain';
import { LanguageSwitcher } from '@components';
import { Logo, Socialbar } from '../';
import { Logo as ECHLogo } from 'ech-components/src/primitives';

const StyledContainer = styled(Container)`
  width: 100%;
  z-index: 1;
`;

const StyledFooter = styled.footer`
  display: flex;
  background-color: ${colors.secondarySoftPurple800};
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const StyledFooterBarContainer = styled.div`
  background-color: ${colors.secondarySoftPurple900};
`;

const StyledFooterBar = styled(StyledContainer)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: ${rem(150)};

  ${mixins.responsiveStyles({
    'flex-direction': { zero: 'column', medium: 'row' },
    'justify-content': { zero: 'center', large: 'flex-start' },
    gap: { zero: rem(32), large: rem(8) },
  })}
`;

const StyledFooterRow = styled.div(
  ({ align }: any) => css`
    display: flex;

    ${align === 'center' &&
    css`
      justify-content: center;
    `}

    hr {
      width: 100%;
    }
  `,
);

const StyledFooterLogoDesktop = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  color: white;
  font-size: ${rem(18)};
  font-weight: 400;

  ${mixins.responsiveStyles({
    'flex-direction': { zero: 'column', medium: 'row', large: 'column' },
    'justify-content': {
      zero: 'flex-start',
      medium: 'space-between',
      large: 'flex-start',
    },
    width: { zero: '100%', large: 'auto' },
    'padding-right': { zero: '0', medium: 20 },
    'min-width': { zero: '0', large: 340, wide: 380, ultra: 460 },
  })}

  address {
    font-style: normal;
  }
`;

const StyledLogo = styled.div`
  svg {
    width: ${rem(200)};
  }
`;

const StyledFooterLogoDesktopCol = styled.div``;

const StyledFooterNav = styled.div`
  display: flex;
  justify-content: space-around;
  flex: 1;

  ${mixins.MQMax('medium')} {
    flex-wrap: wrap;
    gap: ${rem(40)};
  }
`;

const StyledBundesBanner = styled(BundesBanner)`
  width: ${rem(240)};
  margin-left: ${rem(-10)};
`;

const StyledFooterMeta = styled.div`
  flex: 1;

  ${mixins.responsiveStyles({
    'flex-basis': { zero: '100%', large: 'auto' },
  })}

  nav {
    margin-right: ${rem(-10)};
    margin-bottom: ${rem(16)};
    align-items: center;

    ${mixins.responsiveStyles({
      'flex-direction': { zero: 'column-reverse', medium: 'row' },
      'justify-content': { zero: 'center', large: 'flex-end' },
    })}

    a {
      padding: ${rem(8)};
      font-size: ${rem(18)};
      font-weight: 400;

      ${mixins.responsiveStyles({
        'font-size': { zero: rem(16), large: rem(18) },
      })}

      &:hover,
      &:focus {
        color: ${colors.secondarySoftPurple200};
      }
    }
  }
`;

const StyledColBox = styled.div`
  display: inline-flex;
  flex-direction: column;

  ${mixins.responsiveStyles({
    width: { zero: '100%', medium: '33.333%' },
  })}
`;

const StyledNavTitle = styled.strong`
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.075rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: ${rem(12)};
  padding-right: ${rem(32)};

  a {
    color: rgba(255, 255, 255, 0.6);

    &:hover {
      color: ${colors.white};
    }
  }
`;

const StyledNavList = styled.nav`
  display: flex;
  padding-right: ${rem(32)};
  flex-direction: column;
  align-items: flex-start;

  a {
    padding: ${rem(8)} 0;
    font-size: ${rem(18)};
    font-weight: 400;
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;

  &:hover,
  &:focus {
    color: ${colors.secondarySoftPurple200};
  }
`;

const AddressBox = ({ langnode, intl }: any) => {
  return (
    <>
      <StyledFooterLogoDesktop
        itemScope
        itemType="https://schema.org/Organization"
      >
        <StyledFooterLogoDesktopCol>
          <StyledLogo>
            <Logo
              name={langnode.campaignName}
              locale={intl.locale}
              slug={createRoutingURI({
                root: '',
                slug: langnode?.mainHub?.slug,
                locale: intl.locale,
              })}
            />
          </StyledLogo>
          <Spacer space="zeta" />
        </StyledFooterLogoDesktopCol>
        <StyledFooterLogoDesktopCol>
          <span itemProp="name">
            {intl.formatMessage({ id: `page.address.line.one` })}
          </span>
          <address
            itemProp="address"
            itemScope
            itemType="https://schema.org/PostalAddress"
          >
            <span itemProp="alternateName">
              {intl.formatMessage({ id: `page.address.line.two` })}
            </span>
            <br />
            <span itemProp="streetAddress">
              {intl.formatMessage({ id: `page.address.line.three` })}
            </span>
            ,{' '}
            <span itemProp="postalCode">
              {intl.formatMessage({ id: `page.address.line.four` })}
            </span>{' '}
            <span itemProp="addressLocality">
              {intl.formatMessage({ id: `page.address.line.five` })}
            </span>
          </address>
          <Spacer space="mega" />
        </StyledFooterLogoDesktopCol>
      </StyledFooterLogoDesktop>
    </>
  );
};

const Subnav = ({ title, langnode, data, link }: any) => {
  return (
    <>
      <StyledColBox>
        <StyledNavTitle>
          {link ? <a href={link}>{title}</a> : title}
        </StyledNavTitle>
        <Hidden below="medium">
          <Spacer space="zeta" />
        </Hidden>
        <Hidden above="medium">
          <Spacer space="delta" />
        </Hidden>
        <StyledNavList role="menu">
          {data
            ? data.map(
                (
                  item: {
                    slug: string;
                    title: string;
                    navigationTitle: string;
                  },
                  rowIndex: number,
                ) => {
                  return (
                    <StyledLink
                      key={rowIndex}
                      role="menuitem"
                      linkText={
                        item.navigationTitle ? item.navigationTitle : item.title
                      }
                      link={{
                        href: createRoutingURI(
                          getRootSlug(item, langnode.node_locale),
                        ),
                        title: `${
                          item.navigationTitle
                            ? item.navigationTitle
                            : item.title
                        }`,
                      }}
                      color={'light'}
                    />
                  );
                },
              )
            : null}
        </StyledNavList>
        <Hidden below="large">
          <Spacer space="mega" />
        </Hidden>
      </StyledColBox>
    </>
  );
};

const StyledLanguageSwitcher = styled(LanguageSwitcher)`
  flex-direction: column;
  align-items: flex-start;

  a {
    padding: ${rem(8)} 0;
    font-size: ${rem(18)};
    font-weight: 400;
    line-height: 1.5;
  }
`;

const Footer = ({ langObj, intl }: FooterProps) => {
  const { nodes } = useNavigationQuery();
  const langNode = nodes.filter(
    (node: { node_locale: string }) => node.node_locale == intl.locale,
  )[0];
  const echLogoName = intl.formatMessage({
    id: 'nav.echLogo.name',
  });
  const echLogoSlug = intl.formatMessage({
    id: 'nav.echLogo.slug',
  });

  return (
    <>
      <StyledFooter>
        <StyledContainer layout>
          <Spacer space="giga" />
          <StyledFooterRow>
            <Hidden below="large">
              <AddressBox langnode={langNode} intl={intl} />
            </Hidden>
            <StyledFooterNav>
              <Subnav
                title={langNode.groupName3}
                langnode={langNode}
                data={langNode.groupNavigation3}
              />
              <StyledColBox>
                <StyledNavTitle>
                  {intl.formatMessage({ id: `page.footer.language.title` })}
                </StyledNavTitle>
                <Hidden below="medium">
                  <Spacer space="zeta" />
                </Hidden>
                <Hidden above="medium">
                  <Spacer space="delta" />
                </Hidden>
                <StyledLanguageSwitcher langObj={langObj} />
              </StyledColBox>
              <StyledColBox>
                <StyledNavTitle>
                  {intl.formatMessage({ id: `page.footer.social.title` })}
                </StyledNavTitle>
                <Hidden below="medium">
                  <Spacer space="zeta" />
                </Hidden>
                <Hidden above="medium">
                  <Spacer space="delta" />
                </Hidden>
                <Socialbar />
              </StyledColBox>
            </StyledFooterNav>
          </StyledFooterRow>
          <Spacer space="mega" />

          <Hidden above="large">
            <Hidden below="medium">
              <Hr />
              <Spacer space="mega" />
            </Hidden>
            <Hidden above="medium">
              <Spacer space="gamma" />
            </Hidden>
            <StyledFooterRow>
              <AddressBox langnode={langNode} intl={intl} />
            </StyledFooterRow>
          </Hidden>
        </StyledContainer>

        <StyledFooterBarContainer>
          <Hidden above="large">
            <Spacer space="mega" />
          </Hidden>
          <StyledFooterBar layout>
            <StyledBundesBanner
              lang={langObj && langObj.actual ? langObj.actual : 'de'}
            />
            <StyledLogo>
              <ECHLogo
                isLight
                name={echLogoName}
                slug={echLogoSlug}
                locale={intl.locale}
              />
            </StyledLogo>
            <StyledFooterMeta>
              <Metanav
                items={
                  langNode.legalText?.map((item: any) => ({
                    ...item,
                    href: createRoutingURI({
                      slug: item.slug,
                      root: '',
                      locale: item.node_locale,
                    }),
                  })) ?? []
                }
              />
            </StyledFooterMeta>
          </StyledFooterBar>
        </StyledFooterBarContainer>
      </StyledFooter>
    </>
  );
};

export type FooterProps = {
  langObj: {
    actual: string;
    switcher: object[];
  };
  intl: IntlShape;
};

export default injectIntl(Footer);
